import { getUserStoriesQueryOptions } from '@/utils/fetchers/story'
import queryClient from '@/utils/queryClient'
import { type MouseEvent, useRef, useState } from 'react'
import type { Swiper as SwiperInstance } from 'swiper'
import { A11y } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'
import 'swiper/css'
import PersonalStories from './PersonalStories'
import StoryViewerControls from './StoryViewerControls'
import StoryViewerFallback from './StoryViewerFallback'
import styles from './StoryViewer.module.css'

export interface StoryFeedViewerProps {
	username: string
	participantUsernames: readonly string[]
	onClose: () => void
}

export default function StoryFeedViewer({
	username,
	participantUsernames,
	onClose,
}: StoryFeedViewerProps) {
	const personSwiperRef = useRef<SwiperInstance>(null)
	const rememberedStoriesRef = useRef(new Map<string, string>())
	const playbackTimesRef = useRef(new Map<string, number>())
	const readStoriesRef = useRef(new Set<string>())
	const [activePersonIndex, setActivePersonIndex] = useState(0)
	const [isMuted, setIsMuted] = useState(true)
	const initialPersonIndex = Math.max(
		0,
		participantUsernames.findIndex((participantUsername) => participantUsername === username)
	)

	const prefetchAdjacentPeople = (index: number) => {
		for (const participantUsername of [
			participantUsernames[index - 1],
			participantUsernames[index + 1],
		]) {
			if (participantUsername) {
				void queryClient.prefetchInfiniteQuery(getUserStoriesQueryOptions(participantUsername))
			}
		}
	}

	const handlePersonChange = (swiper: SwiperInstance) => {
		personSwiperRef.current = swiper
		setActivePersonIndex(swiper.activeIndex)
		prefetchAdjacentPeople(swiper.activeIndex)
	}

	const handleBackdropClick = (event: MouseEvent<HTMLDivElement>) => {
		if (event.target === event.currentTarget) onClose()
	}

	if (participantUsernames.length === 0) {
		return <StoryViewerFallback status="empty" onClose={onClose} />
	}

	return (
		<div className={styles.root}>
			<Swiper
				className={styles.personSwiper}
				modules={[A11y]}
				cssMode
				initialSlide={initialPersonIndex}
				onSwiper={handlePersonChange}
				onSlideChange={handlePersonChange}
			>
				{participantUsernames.map((participantUsername, personIndex) => (
					<SwiperSlide
						key={participantUsername}
						className={styles.personSlide}
						onClick={handleBackdropClick}
					>
						{({ isActive, isNext, isPrev }) =>
							isActive || isNext || isPrev ? (
								<PersonalStories
									active={isActive}
									canPreviousPerson={personIndex > 0}
									canNextPerson={personIndex < participantUsernames.length - 1}
									username={participantUsername}
									rememberedStories={rememberedStoriesRef.current}
									playbackTimes={playbackTimesRef.current}
									readStories={readStoriesRef.current}
									isMuted={isMuted}
									onMutedChange={setIsMuted}
									onClose={onClose}
									onPersonChange={(direction) =>
										personSwiperRef.current?.[
											direction === 'PREVIOUS' ? 'slidePrev' : 'slideNext'
										]()
									}
								/>
							) : null
						}
					</SwiperSlide>
				))}
			</Swiper>
			<StoryViewerControls
				navigation={{
					canPreviousPerson: activePersonIndex > 0,
					canNextPerson: activePersonIndex < participantUsernames.length - 1,
					onPreviousPerson: () => personSwiperRef.current?.slidePrev(),
					onNextPerson: () => personSwiperRef.current?.slideNext(),
				}}
				onClose={onClose}
			/>
		</div>
	)
}
